import { useWallet } from '@solana/wallet-adapter-react'
import {
  createContext,
  useState,
  FC,
  ReactNode,
  useContext,
  useEffect
} from 'react'
import { useCookies } from 'react-cookie'
import { jwtDecode } from 'jwt-decode'
import { authorize } from '../services/web3Service'

interface AuthContextProps {
  authorized: boolean
  accessToken?: string
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthContext = createContext<AuthContextProps>({
  authorized: false
})

const AuthProvider: FC<AuthProviderProps> = ({
  children
}: AuthProviderProps) => {
  const [authorized, setAuthorized] = useState<boolean>(false)
  const wallet = useWallet()
  const [cookie, setCookie] = useCookies()

  useEffect(() => {
    if (
      wallet.connected &&
      (!cookie.bearer ||
        (jwtDecode(cookie.bearer).exp || 0) < Date.now() / 1000 ||
        jwtDecode(cookie.bearer).sub !== wallet.publicKey?.toBase58())
    ) {
      authorize(wallet).then((accessToken) => {
        // check bearer access token in cookie
        if (accessToken) {
          // set cookie bearer access token
          setCookie('bearer', accessToken)
        }
        setAuthorized(!!accessToken)
      })
    } else {
      setAuthorized(!!cookie.bearer)
    }
  }, [wallet, cookie, setCookie])

  return (
    <AuthContext.Provider
      value={{
        authorized,
        accessToken: cookie.bearer
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider

export const useAuthContext = () => {
  return useContext(AuthContext)
}
